import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Input } from "reactstrap";
import moment from "moment";
import Pagination from "@mui/material/Pagination";
import InputSearch from "../../common/Components/SearchBarField/InputSearch";
import StudioFilterBar, { readFilterSelect } from "../../common/Components/FilterBar/StudioFilterBar";
import GeoResponsiveList from "../../common/Components/GeoList/GeoResponsiveList";
import StudioRowActions, { StudioRowAction } from "../../common/Components/GeoList/StudioRowActions";
import notifications from "../../common/Components/Notifications/Notification";
import { canUseGiRegistry } from "../../helpers/geoEnabled";
import { geoRecordId } from "../gi-registry/geoPath";
import { listFrozenGis } from "../../services/geoGiApi";
import { deleteLoom, getLoomSummary, listLooms, setLoomActive } from "../../services/geoLoomApi";
import GeoPhotoLightbox from "./GeoPhotoLightbox";
import { loomPhotoItems } from "./loomPhotos";
import "../gi-registry/gi-registry.scss";

const LIMIT = 10;

const readAuthUser = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null") || {};
  } catch {
    return {};
  }
};

const LoomList = () => {
  const history = useHistory();
  const authUser: any = readAuthUser();
  const role = authUser?.users?.role;
  const isSuperAdmin = role === "SUPER_ADMIN";
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [loomCode, setLoomCode] = useState("");
  const [giId, setGiId] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const [giOptions, setGiOptions] = useState<{ id: string; name: string }[]>([]);
  const [rows, setRows] = useState<any[]>([]);
  const [totCnt, setTotCnt] = useState(0);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState<any>(null);
  const [busyId, setBusyId] = useState("");
  const [lightboxItems, setLightboxItems] = useState<{ url: string; label: string }[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  const loadList = useCallback(
    async (
      pageOffset = offset,
      codeFilter = loomCode,
      giFilter = giId,
      activeValue = activeFilter
    ) => {
      setLoading(true);
      setError("");
      try {
        const res: any = await listLooms({
          offset: pageOffset,
          limit: LIMIT,
          loomCode: codeFilter.trim() || undefined,
          giId: giFilter || undefined,
          active: activeValue === "true" ? true : activeValue === "false" ? false : undefined,
        });
        if (!res?.success) {
          setRows([]);
          setTotCnt(0);
          setError(res?.message || "Unable to load looms");
          notifications({
            title: "Error!",
            message: res?.message || "Unable to load looms",
            type: "danger",
          });
          return;
        }
        setRows(Array.isArray(res?.data?.docs) ? res.data.docs : []);
        setTotCnt(Number(res?.data?.length) || 0);
      } catch {
        setRows([]);
        setTotCnt(0);
        setError("Unable to load looms");
        notifications({
          title: "Error!",
          message: "Unable to load looms",
          type: "danger",
        });
      } finally {
        setLoading(false);
      }
    },
    [offset, loomCode, giId, activeFilter]
  );

  useEffect(() => {
    if (!canUseGiRegistry(role)) {
      history.replace("/dashboard");
      return;
    }
    loadList(offset, loomCode, giId, activeFilter);
  }, [offset, giId, activeFilter]);

  useEffect(() => {
    if (!canUseGiRegistry(role)) {
      return;
    }
    listFrozenGis().then((res: any) => {
      if (res?.success && Array.isArray(res.data)) {
        setGiOptions(res.data.map((row: any) => ({ id: row.id || row._id, name: row.name })));
      }
    });
    getLoomSummary().then((res: any) => {
      if (res?.success) {
        setSummary(res.data);
      }
    });
  }, [role]);

  const handleSearch = () => {
    setOffset(0);
    loadList(0, loomCode, giId, activeFilter);
  };

  const handleReset = () => {
    setLoomCode("");
    setGiId("");
    setActiveFilter("");
    setOffset(0);
    loadList(0, "", "", "");
  };

  const handlePageChange = (_event: any, value: any) => {
    setOffset(value - 1);
  };

  const openPhotos = (row: any, index: number) => {
    const items = loomPhotoItems(row);
    if (items.length === 0) {
      return;
    }
    setLightboxItems(items);
    setLightboxIndex(index);
  };

  const refreshSummary = () => {
    getLoomSummary().then((res: any) => {
      if (res?.success) {
        setSummary(res.data);
      }
    });
  };

  const handleToggleActive = async (row: any) => {
    const id = geoRecordId(row);
    if (!id) {
      return;
    }
    const nextActive = row?.active === false;
    setBusyId(id);
    try {
      const res: any = await setLoomActive(id, nextActive);
      if (!res?.success) {
        notifications({
          title: "Error!",
          message: res?.message || "Could not update loom status",
          type: "danger",
        });
        return;
      }
      notifications({
        title: "Success!",
        message: nextActive ? "Loom marked active" : "Loom marked inactive",
        type: "success",
      });
      loadList(offset, loomCode, giId, activeFilter);
      refreshSummary();
    } catch {
      notifications({
        title: "Error!",
        message: "Could not update loom status",
        type: "danger",
      });
    } finally {
      setBusyId("");
    }
  };

  const handleDelete = async (row: any) => {
    const id = geoRecordId(row);
    if (!id) {
      return;
    }
    if (!window.confirm(`Delete loom ${row?.loomCode || ""}? This cannot be undone.`)) {
      return;
    }
    setBusyId(id);
    try {
      const res: any = await deleteLoom(id);
      if (!res?.success) {
        notifications({
          title: "Error!",
          message: res?.message || "Could not delete this loom",
          type: "danger",
        });
        return;
      }
      notifications({
        title: "Success!",
        message: "Loom deleted",
        type: "success",
      });
      loadList(offset, loomCode, giId, activeFilter);
      refreshSummary();
    } catch {
      notifications({
        title: "Error!",
        message: "Could not delete this loom",
        type: "danger",
      });
    } finally {
      setBusyId("");
    }
  };

  const totPages = Math.ceil(totCnt / LIMIT) || 0;
  const emptyText =
    error && !loading && rows.length === 0
      ? error
      : loading && rows.length === 0
      ? "Loading looms…"
      : "No looms tagged yet.";

  const loomActions = (ele: any) => {
    const id = geoRecordId(ele);
    const actions: StudioRowAction[] = [
      {
        key: "view",
        label: "View",
        icon: "view",
        onClick: () => history.push(`/looms/${id}`),
      },
      {
        key: "edit",
        label: "Edit",
        icon: "edit",
        tone: "primary",
        onClick: () => history.push(`/looms/${id}/edit`),
      },
      {
        key: "toggle",
        label: ele?.active === false ? "Mark active" : "Mark inactive",
        icon: "toggle",
        disabled: busyId === id,
        onClick: () => handleToggleActive(ele),
      },
      {
        key: "delete",
        label: "Delete",
        icon: "delete",
        tone: "danger",
        disabled: busyId === id,
        onClick: () => handleDelete(ele),
      },
    ];
    return <StudioRowActions actions={actions} />;
  };

  const loomPhotos = (ele: any) => {
    const photos = loomPhotoItems(ele);
    if (photos.length === 0) {
      return "—";
    }
    return (
      <div className="geo-table-thumbs">
        {photos.map((photo, idx) => (
          <button
            type="button"
            key={`${photo.url}-${idx}`}
            onClick={() => openPhotos(ele, idx)}
            aria-label={`View ${photo.label} photo`}
          >
            <img src={photo.url} alt={photo.label} />
          </button>
        ))}
      </div>
    );
  };

  const loomColumns = [
    {
      key: "photos",
      header: "Photos",
      className: "geo-col-photos",
      cell: (ele: any) => loomPhotos(ele),
    },
    {
      key: "loomCode",
      header: "Loom ID",
      className: "geo-col-loomid",
      cell: (ele: any) => (
        <>
          {ele?.loomCode || "—"}
          {ele?.studioLoomCode ? <div className="text-muted small">Studio loom {ele.studioLoomCode}</div> : null}
        </>
      ),
    },
    {
      key: "gi",
      header: "GI",
      className: "geo-col-gi",
      cell: (ele: any) => ele?.giName || "—",
    },
    ...(isSuperAdmin
      ? [
          {
            key: "studio",
            header: "Studio",
            hideSm: true,
            cell: (ele: any) => ele?.orgName || "—",
          },
        ]
      : []),
    {
      key: "fence",
      header: "Fence",
      cell: (ele: any) =>
        !ele?.giId ? (
          <span className="gi-status-chip">{ele?.geoTagStatus === "NONE" ? "Not geotagged yet" : "No GI chosen yet"}</span>
        ) : (
          <span className={ele?.insideGi ? "gi-status-chip is-frozen" : "gi-status-chip is-rejected"}>
            {ele?.distanceLabel || (ele?.insideGi ? "Inside" : "Outside")}
          </span>
        ),
    },
    {
      key: "status",
      header: "Status",
      cell: (ele: any) => (
        <span className={ele?.active === false ? "gi-status-chip is-rejected" : "gi-status-chip is-frozen"}>
          {ele?.active === false ? "Inactive" : "Active"}
        </span>
      ),
    },
    {
      key: "sticker",
      header: "Sticker",
      hideSm: true,
      cell: (ele: any) => (ele?.qrCode ? "Bound" : "—"),
    },
    {
      key: "tagged",
      header: "Tagged",
      hideSm: true,
      cell: (ele: any) => (ele?.createdAt ? moment(new Date(ele.createdAt)).format("YYYY-MM-DD") : "—"),
    },
  ];

  return (
    <div className="gi-registry-page">
      <div className="gi-toolbar-row">
        <h2>Looms</h2>
        <StudioFilterBar
          fields={[
            {
              id: "loomCode",
              label: "Loom ID",
              placeholder: "Loom ID",
              value: loomCode,
              onChange: (value: string) => setLoomCode(value),
            },
            {
              id: "giId",
              label: "GI",
              kind: "select",
              placeholder: "All GIs",
              value: giId,
              options: giOptions.map((opt) => ({ label: opt.name, value: opt.id })),
              onChange: (value: any) => {
                setGiId(readFilterSelect(value));
                setOffset(0);
              },
            },
            {
              id: "active",
              label: "Status",
              kind: "select",
              placeholder: "Status",
              value: activeFilter,
              options: [
                { label: "Active", value: "true" },
                { label: "Inactive", value: "false" },
              ],
              onChange: (value: any) => {
                setActiveFilter(readFilterSelect(value));
                setOffset(0);
              },
            },
          ]}
          defaultFieldId="loomCode"
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              handleSearch();
            }
          }}
          onReset={handleReset}
          addTo="/looms/new"
          addAriaLabel="GeoTag Looms"
          wideToolbar={
            <div className="gi-registry-filters gi-registry-filters--loom">
              <div className="geo-filter-fields">
                <InputSearch
                  name="loomCode"
                  placeholder="Loom ID"
                  value={loomCode}
                  onChange={(e: any) => setLoomCode(e.target.value)}
                  onKeyDown={(e: any) => {
                    if (e.key === "Enter") {
                      handleSearch();
                    }
                  }}
                />
                <Input
                  type="select"
                  className="geo-filter-gi"
                  value={giId}
                  onChange={(e) => {
                    setGiId(e.target.value);
                    setOffset(0);
                  }}
                >
                  <option value="">All GIs</option>
                  {giOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.name}
                    </option>
                  ))}
                </Input>
              </div>
              <Input
                type="select"
                className="geo-filter-status"
                value={activeFilter}
                onChange={(e) => {
                  setActiveFilter(e.target.value);
                  setOffset(0);
                }}
              >
                <option value="">Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </Input>
              <Button className="ResetBtn" onClick={handleSearch}>
                Search
              </Button>
              <Button className="btn-hastkala-outline" onClick={handleReset}>
                Reset
              </Button>
              <Button className="ResetBtn geo-toolbar-cta" onClick={() => history.push("/looms/new")}>
                GeoTag Looms
              </Button>
            </div>
          }
        />
      </div>
      {summary ? (
        <div className="gi-summary-strip">
          <span>Looms {summary.loomTotal ?? 0}</span>
          <span>Active {summary.loomActive ?? 0}</span>
          <span>Inactive {summary.loomInactive ?? 0}</span>
          <span>Outside fence {summary.loomOutside ?? 0}</span>
        </div>
      ) : null}
      <GeoResponsiveList
        rows={rows}
        rowKey={(ele) => geoRecordId(ele) || ele.loomCode}
        columns={loomColumns}
        empty={emptyText}
        tableClassName="geo-table--looms"
        actions={(ele) => loomActions(ele)}
      />
      {totPages > 1 ? (
        <div className="geo-pagination">
          <Pagination count={totPages} page={offset + 1} onChange={handlePageChange} />
        </div>
      ) : null}
      <GeoPhotoLightbox
        items={lightboxItems}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(-1)}
        onIndex={setLightboxIndex}
      />
    </div>
  );
};

export default LoomList;
